import React, { useState, useRef, useEffect } from 'react';
import { CalendarIcon } from '@heroicons/react/outline';

const ToggleSwitch = ({ enabled, onChange, label, disabled }) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      disabled={disabled}
    >
      <div
        className={`block h-6 w-11 rounded-full transition-colors duration-200 ease-in-out ${
          enabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      />
      <div
        className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
    <span className={`text-gray-700 dark:text-gray-200 whitespace-nowrap min-w-[80px] text-right ${disabled ? 'opacity-60' : ''}`}>{label}</span>
  </div>
);

const SectionDivider = ({ title, color = 'blue' }) => {
  const colors = {
    blue: 'border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400',
    green: 'border-green-200 dark:border-green-800 text-green-600 dark:text-green-400',
    purple: 'border-purple-200 dark:border-purple-800 text-purple-600 dark:text-purple-400'
  };

  const colorClasses = colors[color] || colors.blue;

  return (
    <div className="relative my-8">
      <div className="absolute inset-0 flex items-center">
        <div className={`w-full border-t ${colorClasses.split(' ')[0]} ${colorClasses.split(' ')[1]}`}></div>
      </div>
      <div className="relative flex justify-center">
        <span className={`bg-white dark:bg-gray-800 px-4 text-lg font-semibold ${colorClasses.split(' ')[2]} ${colorClasses.split(' ')[3]}`}>
          {title}
        </span>
      </div>
    </div>
  );
};

const DateTimeInput = ({ value, onChange, label, disabled, min }) => {
  const inputRef = useRef(null);
  
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toISOString().slice(0, 16);
  };

  const handleDateChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <div>
      <label className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="datetime-local"
          value={formatDateForInput(value)}
          onChange={handleDateChange}
          min={min}
          className={`input-base w-full pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-10 ${
            disabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''
          }`}
          disabled={disabled}
          required
        />
        <div 
          className={`absolute inset-y-0 right-0 pr-3 flex items-center ${!disabled && 'cursor-pointer hover:text-blue-600'}`}
          onClick={() => !disabled && inputRef.current?.showPicker()}
        >
          <CalendarIcon className={`h-5 w-5 ${disabled ? 'text-gray-400' : 'text-blue-500'}`} />
        </div>
      </div>
    </div>
  );
};

const CourseOptions = ({ data, categories = [], updateData }) => {
  const [hasDiscount, setHasDiscount] = useState(data.discountValue ? true : false);
  const [discountType, setDiscountType] = useState(data.discountType || 'percentage');

  useEffect(() => {
    // بيانات خيارات الدورة
    
    // تحديث حالة التبديل بناءً على نوع الدورة
    if (data.courseType === 'paid') {
      // إذا كانت الدورة مدفوعة، تأكد من أن السعر ليس صفر
      if (parseFloat(data.price) <= 0) {
        // إذا كان السعر صفر، قم بتحديثه إلى قيمة افتراضية
        updateData({ price: '1' });
      }
    }
    
    // تحديث حالة الخصم عند تغيير البيانات
    if (data.discountValue && !hasDiscount) {
      setHasDiscount(true);
    }
    
    if (data.discountType) {
      setDiscountType(data.discountType);
    }
  }, [data, updateData, hasDiscount]);

  return (
    <div className="space-y-6">
      {/* معلومات الدورة الأساسية */}
      <SectionDivider title="معلومات الدورة الأساسية" color="green" />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* البطاقة اليسرى */}
        <div className="p-6 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 rounded-lg h-full">
          {/* التصنيف */}
          <div className="mb-6">
            <label htmlFor="category" className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
              التصنيف <span className="text-red-500">*</span>
            </label>
            <select
              id="category"
              value={data.category}
              onChange={(e) => updateData({ category: e.target.value })}
              className="input-base h-[38px] py-1 w-full"
              required
            >
              <option value="">اختر التصنيف</option>
              {categories && categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* المدة الزمنية للدورة */}
          <div>
            <label htmlFor="duration" className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
              المدة <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                id="duration"
                value={data.duration}
                onChange={(e) => updateData({ duration: e.target.value })}
                className="input-base pl-20 w-full"
                placeholder="أدخل مدة الدورة"
                min="1"
                required
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 dark:text-gray-400">ساعة</span>
              </div>
            </div>
          </div>
        </div>

        {/* البطاقة اليمنى */}
        <div className="p-6 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 rounded-lg h-full">
          {/* نوع الدورة */}
          <div className="mb-6">
            <label className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-4">
              نوع الدورة <span className="text-red-500">*</span>
            </label>
            <ToggleSwitch
              enabled={data.courseType === 'paid'}
              onChange={(enabled) => updateData({ 
                courseType: enabled ? 'paid' : 'free',
                price: enabled ? (data.price || '0') : '0'
              })}
              label={data.courseType === 'paid' ? 'مدفوعة' : 'مجانية'}
            />
          </div>

          {/* السعر */}
          <div>
            <label htmlFor="price" className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
              السعر <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                id="price"
                value={data.price || '0'}
                onChange={(e) => updateData({ price: e.target.value })}
                className={`input-base pl-16 w-full ${data.courseType !== 'paid' ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''}`}
                placeholder="أدخل سعر الدورة"
                min="0"
                disabled={data.courseType !== 'paid'}
                required
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 dark:text-gray-400">ريال</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* التسعير والدعم */}
      <SectionDivider title="التسعير والدعم" color="purple" />
      
      {/* البطاقات الإضافية */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* البطاقة اليسرى */}
        <div className="p-6 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 rounded-lg h-full">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8">
            {/* اسأل مدرب */}
            <div className="flex flex-col items-start">
              <label className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-3">
                اسأل مدرب
              </label>
              <ToggleSwitch
                enabled={data.askInstructor === 'enabled'}
                onChange={(enabled) => updateData({ askInstructor: enabled ? 'enabled' : 'disabled' })}
              />
            </div>

            {/* تفعيل الخصم */}
            <div className="flex flex-col items-start">
              <label className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-3">
                تفعيل الخصم
              </label>
              <ToggleSwitch
                enabled={hasDiscount}
                onChange={(enabled) => {
                  setHasDiscount(enabled);
                  updateData({
                    hasDiscount: enabled,
                    discountValue: enabled ? (data.discountValue || '') : '',
                    discountStart: enabled ? (data.discountStart || '') : '',
                    discountEnd: enabled ? (data.discountEnd || '') : '',
                    discountType: enabled ? discountType : 'percentage'
                  });
                }}
              />
            </div>

            {/* نوع الخصم */}
            <div className="flex flex-col items-start">
              <label className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-3">
                نوع الخصم
              </label>
              <ToggleSwitch
                enabled={discountType === 'percentage'}
                onChange={(enabled) => {
                  const newType = enabled ? 'percentage' : 'fixed';
                  setDiscountType(newType);
                  updateData({
                    discountType: newType,
                    discountValue: ''
                  });
                }}
                label={discountType === 'percentage' ? 'نسبة مئوية' : 'مبلغ ثابت'}
                disabled={!hasDiscount}
              />
            </div>
          </div>

          {/* قيمة الخصم */}
          <div className="relative mt-6">
            <label htmlFor="discountValue" className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
              قيمة الخصم
            </label>
            <input
              type="number"
              id="discountValue"
              value={data.discountValue}
              onChange={(e) => updateData({ discountValue: e.target.value })}
              className="input-base pl-16 w-full"
              placeholder="أدخل قيمة الخصم"
              disabled={!hasDiscount}
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none top-8">
              <span className="text-gray-500 dark:text-gray-400">
                {discountType === 'fixed' ? 'ريال' : '%'}
              </span>
            </div>
          </div>
        </div>

        {/* البطاقة اليمنى */}
        <div className="p-6 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 rounded-lg h-full">
          <div className="space-y-6">
            {/* تاريخ بداية الخصم */}
            <DateTimeInput
              label="تاريخ بداية الخصم"
              value={data.discountStart}
              onChange={(value) => updateData({ discountStart: value, discount_start_date: value })}
              disabled={!hasDiscount}
            />

            {/* تاريخ نهاية الخصم */}
            <DateTimeInput
              label="تاريخ نهاية الخصم"
              value={data.discountEnd}
              onChange={(value) => updateData({ discountEnd: value, discount_end_date: value })}
              disabled={!hasDiscount}
              min={data.discountStart}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseOptions;
